import { yupResolver } from '@hookform/resolvers/yup';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer';
import { scrollToError } from '@openx/components-core/utils/scrollToError';
import { onKeyDown } from '@openx/components-form/onKeyDown';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CuratedDealBuyerDiscountApi } from '../../../types/curatedDealBuyerDiscount';
import { CuratedDealBuyerDiscountsFormSections } from './CuratedDealBuyerDiscountsFormSections';
import { curatedDealBuyerDiscountsFormDefaultValues } from './constants';
import { useCuratedDealBuyerDiscountFormSubmit } from './hooks/useCuratedDealBuyerDiscountFormSubmit';
import type { CuratedDealBuyerDiscountSeat, CuratedDealBuyerDiscountsFormData } from './types';
import { createCuratedDealBuyerDiscountFormData } from './utils/createCuratedDealBuyerDiscountFormData';
import { getCuratedDealBuyerDiscountsValidationSchema } from './validationSchema';

type CuratedDealBuyerDiscountsFormProps = {
  curatedDealBuyerDiscount?: CuratedDealBuyerDiscountApi;
  demandPartnerName: string | null;
  seatById: Record<string, CuratedDealBuyerDiscountSeat>;
};

export const CuratedDealBuyerDiscountsForm = ({
  curatedDealBuyerDiscount,
  demandPartnerName,
  seatById,
}: CuratedDealBuyerDiscountsFormProps) => {
  const isEdit = !!curatedDealBuyerDiscount;
  const { t } = useTranslation();

  const defaultValues = useMemo(() => {
    if (isEdit) {
      return createCuratedDealBuyerDiscountFormData(curatedDealBuyerDiscount, demandPartnerName, seatById);
    }

    return curatedDealBuyerDiscountsFormDefaultValues;
  }, [isEdit, curatedDealBuyerDiscount, demandPartnerName, seatById]);

  const validationSchema = useMemo(() => getCuratedDealBuyerDiscountsValidationSchema(t), [t]);

  const { submitHandler } = useCuratedDealBuyerDiscountFormSubmit(defaultValues, curatedDealBuyerDiscount?.uid);
  const formMethods = useForm<CuratedDealBuyerDiscountsFormData>({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(validationSchema),
  });

  return (
    <FormProvider {...formMethods}>
      <form autoComplete="off" onKeyDown={onKeyDown} onSubmit={formMethods.handleSubmit(submitHandler, scrollToError)}>
        <FormContainer fullPage>
          <CuratedDealBuyerDiscountsFormSections isEdit={isEdit} seatIds={curatedDealBuyerDiscount?.seat_ids} />
        </FormContainer>
      </form>
    </FormProvider>
  );
};
